/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { BaseComponent } from '../index';
import { Component, OnInit } from '@angular/core';
import { DownloadService, LogService } from '../../services';
import { HttpEventType } from '@angular/common/http';
import { merge, Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';

@Component({
    selector: 'app-progress-bar',
    templateUrl: './progress-bar.component.html',
    styleUrls: ['./progress-bar.component.scss']
})

export class ProgressBarComponent extends BaseComponent implements OnInit {
    /**
     * Progress bar prefix text.
     */
    public progressPrefixText: string = 'downloading';

    /**
     * Show spinner
     */
    public spinner: boolean = false;

    /**
     * Show progress bar.
     */
    public bar: boolean = false;

    /**
     * Show percentage under progress bar.
     */
    public showPercentage: boolean = true;

    /**
     * Show transfer rate under progress bar.
     */
    public showTransferRate: boolean = false;

    /**
     * Show time remaining
     */
    public showTimeRemaining: boolean = false;

    /**
     * Progress bar percentage.
     */
    public percentDone: number = 0;

    /**
     * Progress bar transfer rate.
     */
    public transferRate: number = 0;

    /**
     * Progress bar time remaining.
     */
    public timeRemaining: string = '';

    /**
     * Store the filename from the fileUrl.
     */
    public fileName: string = '';

    /**
     * Url of file to download to the server
     */
    private fileUrl: string;

    /**
     * FileType.
     * @private
     */
    private fileType: string = 'blob';

    /**
     * Don't download anything, just show a timed progress bar.
     * @private
     */
    private timedProgressBarTime: number;

    /**
     * On complete callback function.
     * @private
     */
    private onCompleteCallBack: (...args: any[]) => void;

    /**
     * On cancel callback function.
     * @private
     */
    private onCancelCallBack: (...args: any[]) => void;

    /**
     * User cancels request.
     * @private
     */
    private userCancel$: Subject<boolean> = new Subject<boolean>();

    constructor(
        private downloadService: DownloadService,
        private logService: LogService
    ) {
        super();
    }

    public ngOnInit() {
        if (this.fileUrl) {
            this.setFilename(this.fileUrl);
            this.triggerDownload(this.fileUrl);
        } else if (this.timedProgressBarTime) {
            this.startTimedProgressBar();
        } else {
            this.logService.warn('ProgressDownloadComponent: ngOnInit', 'No filename or timer set');
        }
    }

    /**
     * Start the download
     */
    public startTimedProgressBar(): void {
        const timer = setInterval(() => {
            this.percentDone++;
            if (this.percentDone >= 100) {
                if (this.onCompleteCallBack) {
                    this.onCompleteCallBack();
                }

                clearInterval(timer);
            }
        }, this.timedProgressBarTime);
    }

    /**
     * User cancels download.
     */
    public userCancel(): void {
        this.userCancel$.next(true);
        this.onCancelCallBack();
    }

    /**
     * Sets filename from file url.
     * @param fileUrl
     * @private
     */
    private setFilename(fileUrl: string) {
        const fileUrlParts = fileUrl.split('/');
        this.fileName = fileUrlParts[fileUrlParts.length - 1];
    }

    /**
     * Start the download
     * @param url
     * @private
     */
    private triggerDownload(url: string): void {
        let firstRun = true;
        const startTime = new Date().getTime();
        this.logService.debug('ProgressBarComponent: triggerDownload', 'Download triggered');

        const stopConditions$ = merge(this.userCancel$, this.destroyed$).pipe(
            tap(() => this.logService.warn('ProgressBarComponent:triggerDownload', 'User cancelled download or download completed'))
        );

        this.downloadService.download(url, this.fileType)
            .pipe(takeUntil(stopConditions$))
            .subscribe({
                next: (result): void => {
                    if (firstRun) {
                        firstRun = false;
                        this.logService.debug('ProgressBarComponent: triggerDownload', 'Got first response');
                    }

                    if (result.type === HttpEventType.DownloadProgress) {
                        this.updateDownloadProgress(result, startTime);
                    }
                    if (result.type === HttpEventType.Response) {
                        this.logService.debug(
                            'ProgressBarComponent: triggerDownload', 'Got complete response'
                        );

                        this.downloadService.generateDownload(result.body, this.fileName, { callBack: this.callBackHelper(startTime) });
                    }
                },
                error: (error: any): void => {
                    this.logService.error('ProgressBarComponent: triggerDownload', error);
                }
            });
    }

    /**
     * Update download progress
     * @param result
     * @param startTime
     * @private
     */
    private updateDownloadProgress(result: any, startTime: number): void {
        if (this.showPercentage) {
            this.percentDone = Math.round(100 * result.loaded / result.total);
        }

        if (this.showTransferRate) {
            const duration = (new Date().getTime() - startTime) / 1000;
            const bps = result.loaded / duration;
            const kbps = bps / 1024;
            this.transferRate = Math.floor(kbps);

            if (this.showTimeRemaining) {
                const time = (result.total - result.loaded) / bps;
                const seconds = Math.floor(time % 60);
                const minutes = Math.floor(time / 60);
                this.timeRemaining = minutes + ' minutes ' + seconds + ' seconds remaining';
            }
        }
    }

    /**
     * If the download time is less than a second delay the callback.
     * @param startTime
     * @private
     */
    private callBackHelper(startTime: number): any {
        const endTime = new Date().getTime();
        if (endTime - startTime < 1000) {
            const timerId = setTimeout(() => {
                this.onCompleteCallBack();
                clearTimeout(timerId);
            }, 2500);
        } else {
            this.onCompleteCallBack();
        }
    }
}
